import { getLiteral, getLiteralPluralized, getLiteralWithParameters } from 'utils/getLiteral';
import { MARKETING_CAMPAIGN_ACTIVITY } from 'constants/Entities';

export const MARKETING_CAMPAIGN_EXPORT_SCHEMA_ID = MARKETING_CAMPAIGN_ACTIVITY.trueName;

const INTERACTION_TYPE_LITERALS = {
    delivered: 'label_marketing_campaigns_metrics_delivered_plural',
    open: 'label_marketing_campaigns_metrics_opened_plural',
    click: 'label_marketing_campaigns_metrics_clicked_plural',
    bounced: 'label_marketing_campaigns_metrics_bounces',
    unsubscribe: 'label_marketing_campaigns_metrics_unsubscribed',
};

export const getMarketingCampaignInteractionLabel = (type) => {
    const literalKey = INTERACTION_TYPE_LITERALS[type];
    return literalKey ? getLiteral(literalKey) : type;
};

export const getMarketingCampaignMetricsSummary = (globalMetrics) => {
    if (!globalMetrics) return null;

    const parts = [
        {
            key: 'opens',
            label: getLiteral('label_marketing_campaigns_metrics_unique_opens'),
            value: globalMetrics.OpensUnique,
        },
        {
            key: 'clicks',
            label: getLiteral('label_marketing_campaigns_metrics_unique_clicks'),
            value: globalMetrics.ClicksUnique,
        },
        {
            key: 'bounces',
            label: getLiteral('label_marketing_campaigns_metrics_bounces'),
            value: globalMetrics.Bounces,
        },
        {
            key: 'unsubscribes',
            label: getLiteral('label_marketing_campaigns_metrics_unsubscribed'),
            value: globalMetrics.Unsubscribes,
        },
    ].filter(({ value }) => value !== undefined && value !== null);

    if (!parts.length) return null;

    return parts.map(({ label, value }) => `${label}: ${value}`).join(' · ');
};

export const getMarketingCampaignRecipientsSummary = (recipients) => {
    if (!recipients) return null;

    const parts = [
        recipients.Total !== undefined &&
            recipients.Total !== null && {
                label: getLiteral('label_total'),
                value: recipients.Total,
            },
        recipients.Contacts !== undefined &&
            recipients.Contacts !== null && {
                text: getLiteralPluralized(
                    recipients.Contacts,
                    'label_one_contact',
                    'label_contacts',
                ),
            },
        recipients.Accounts !== undefined &&
            recipients.Accounts !== null && {
                text: getLiteralPluralized(
                    recipients.Accounts,
                    'label_one_account',
                    'label_accounts',
                ),
            },
    ].filter(Boolean);

    if (!parts.length) return null;

    return parts.map(({ label, value, text }) => text || `${label}: ${value}`).join(' · ');
};

export const getMarketingCampaignCardMetrics = (globalMetrics) => {
    if (!globalMetrics) return [];

    return [
        {
            icon: 'visibilityOn',
            value: globalMetrics.OpensUnique,
            labelKey: 'label_marketing_campaigns_activity_metric_opened',
        },
        {
            icon: 'trackingLink',
            value: globalMetrics.ClicksUnique,
            labelKey: 'label_marketing_campaigns_activity_metric_clicks',
        },
        {
            icon: 'close',
            value: globalMetrics.Unsubscribes,
            labelKey: 'label_marketing_campaigns_activity_metric_unsubscribed',
        },
    ].filter(({ value }) => value !== undefined && value !== null);
};

const METRIC_LABEL_FALLBACKS = {
    label_marketing_campaigns_activity_metric_opened:
        'label_marketing_campaigns_metrics_opened_plural',
    label_marketing_campaigns_activity_metric_clicks:
        'label_marketing_campaigns_metrics_clicked_plural',
    label_marketing_campaigns_activity_metric_unsubscribed:
        'label_marketing_campaigns_metrics_unsubscribed',
};

export const formatMarketingCampaignMetricLabel = (value, labelKey) => {
    const template = getLiteral(labelKey);

    if (template.includes('%@')) {
        return getLiteralWithParameters(labelKey, [value]);
    }

    if (template !== labelKey) {
        return `${value} ${template.toLowerCase()}`;
    }

    const fallbackKey = METRIC_LABEL_FALLBACKS[labelKey];
    if (fallbackKey) {
        return `${value} ${getLiteral(fallbackKey).toLowerCase()}`;
    }

    return `${value}`;
};

export const getMarketingCampaignRecipientsBreakdown = (recipients) => {
    if (!recipients) return null;

    const breakdown = [
        recipients.Contacts !== undefined &&
            recipients.Contacts !== null && {
                text: getLiteralPluralized(
                    recipients.Contacts,
                    'label_one_contact',
                    'label_number_contacts',
                ),
            },
        recipients.Accounts !== undefined &&
            recipients.Accounts !== null && {
                text: getLiteralPluralized(
                    recipients.Accounts,
                    'label_one_account',
                    'label_number_accounts',
                ),
            },
        recipients.Lists !== undefined &&
            recipients.Lists !== null && {
                text: getLiteralPluralized(
                    recipients.Lists,
                    'label_one_list',
                    'label_number_lists',
                ),
            },
    ].filter(Boolean);

    return {
        total: recipients.Total,
        breakdown,
    };
};
